import React, { useEffect, useState } from "react";
import { INotice } from "../../exports/interfaces";
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import templatePdfUrl from "../../assets/template.pdf";
import { useNavigate } from "react-router-dom";
import { usePermission } from "../../hooks/usePermission";

const NoticeViewPopUp = ({
  show,
  onClose,
  notice,
}: {
  show: boolean;
  onClose: () => void;
  notice: INotice | null;
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { canEdit } = usePermission();
  const navigate = useNavigate();

  useEffect(() => {
    if (!show || !notice) {
      setPdfUrl(null);
      return;
    }

    const generatePdf = async () => {
      setLoading(true);
      try {
        // Load the template PDF
        const templateBytes = await fetch(templatePdfUrl).then((res) =>
          res.arrayBuffer(),
        );
        const pdfDoc = await PDFDocument.load(templateBytes);

        // Get the first page
        const pages = pdfDoc.getPages();
        const page = pages[0];
        const { width, height } = page.getSize();

        // Embed a standard font (Helvetica for Latin text)
        const fontBold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
        const fontRegular = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const darkBlue = rgb(0, 0, 0);
        const black = rgb(0, 0, 0);

        // Starting Y position — below the template header
        // Adjust this value based on where the template header ends
        let currentY = height - 170;

        // 1. Notice Type — centered
        const noticeTypeText = notice.noticeType || "";
        const noticeTypeFontSize = 16;
        const noticeTypeWidth = fontBold.widthOfTextAtSize(
          noticeTypeText,
          noticeTypeFontSize,
        );
        page.drawText(noticeTypeText, {
          x: (width - noticeTypeWidth) / 2,
          y: currentY,
          size: noticeTypeFontSize,
          font: fontBold,
          color: darkBlue,
        });

        currentY -= 20;

        // 2. Notice To — left aligned
        const noticeToLabel = "To: ";
        const noticeToValue = notice.noticeTo || "";
        const toFontSize = 10;
        const leftMargin = 50;

        page.drawText(noticeToLabel, {
          x: leftMargin,
          y: currentY,
          size: toFontSize,
          font: fontBold,
          color: darkBlue,
        });

        const labelWidth = fontBold.widthOfTextAtSize(
          noticeToLabel,
          toFontSize,
        );
        page.drawText(noticeToValue, {
          x: leftMargin + labelWidth,
          y: currentY,
          size: toFontSize,
          font: fontRegular,
          color: black,
        });

        currentY -= 20;

        // 3. Notice Title — left aligned
        const titleLabel = "Notice Title: ";
        const titleValue = notice.noticeTitle || "";
        const titleFontSize = 10;

        page.drawText(titleLabel, {
          x: leftMargin,
          y: currentY,
          size: titleFontSize,
          font: fontBold,
          color: darkBlue,
        });

        const titleLabelWidth = fontBold.widthOfTextAtSize(
          titleLabel,
          titleFontSize,
        );
        page.drawText(titleValue, {
          x: leftMargin + titleLabelWidth,
          y: currentY,
          size: titleFontSize,
          color: black,
        });

        // Draw underline below title
        currentY -= 8;
        page.drawLine({
          start: { x: leftMargin, y: currentY },
          end: { x: width - leftMargin, y: currentY },
          thickness: 0.5,
          color: rgb(0.8, 0.8, 0.8),
        });

        currentY -= 15;

        // 4. Notice Details — justified, wrapped text
        const detailsFontSize = 10;
        const maxLineWidth = width - leftMargin * 2;
        const detailsText = notice.noticeDetails || "";
        const lineHeight = detailsFontSize + 6;

        // Word-wrap considering newlines, track paragraph-end lines
        const finalLines: { words: string[]; isLastInParagraph: boolean }[] =
          [];
        for (const paragraph of detailsText.split("\n")) {
          const pWords = paragraph.split(/\s+/).filter(Boolean);
          if (pWords.length === 0) {
            finalLines.push({ words: [], isLastInParagraph: true });
            continue;
          }
          let lineWords: string[] = [];
          for (const word of pWords) {
            const testLine = lineWords.length
              ? `${lineWords.join(" ")} ${word}`
              : word;
            const testWidth = fontRegular.widthOfTextAtSize(
              testLine,
              detailsFontSize,
            );
            if (testWidth > maxLineWidth && lineWords.length > 0) {
              finalLines.push({ words: lineWords, isLastInParagraph: false });
              lineWords = [word];
            } else {
              lineWords.push(word);
            }
          }
          if (lineWords.length > 0) {
            finalLines.push({ words: lineWords, isLastInParagraph: true });
          }
        }

        // Draw lines — justified for full lines, left-aligned for last-in-paragraph
        for (const { words: lineWords, isLastInParagraph } of finalLines) {
          if (currentY < 50) break;

          // Empty line (blank paragraph)
          if (lineWords.length === 0) {
            currentY -= lineHeight;
            continue;
          }

          // Last line of paragraph or single-word line → left-aligned
          if (isLastInParagraph || lineWords.length === 1) {
            page.drawText(lineWords.join(" "), {
              x: leftMargin,
              y: currentY,
              size: detailsFontSize,
              font: fontRegular,
              color: black,
            });
          } else {
            // Justified: distribute extra space evenly between words
            const totalTextWidth = lineWords.reduce(
              (sum, w) =>
                sum + fontRegular.widthOfTextAtSize(w, detailsFontSize),
              0,
            );
            const extraSpace =
              (maxLineWidth - totalTextWidth) / (lineWords.length - 1);
            let xPos = leftMargin;
            for (const word of lineWords) {
              page.drawText(word, {
                x: xPos,
                y: currentY,
                size: detailsFontSize,
                font: fontRegular,
                color: black,
              });
              xPos +=
                fontRegular.widthOfTextAtSize(word, detailsFontSize) +
                extraSpace;
            }
          }
          currentY -= lineHeight;
        }

        // Save the modified PDF
        const pdfBytes = await pdfDoc.save();
        const blob = new Blob([new Uint8Array(pdfBytes)], {
          type: "application/pdf",
        });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (error) {
        console.error("Error generating notice PDF:", error);
      } finally {
        setLoading(false);
      }
    };

    generatePdf();

    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, notice]);

  if (!show || !notice) return null;

  return (
    <div className="doc-modal-overlay" onClick={onClose}>
      <div
        className="doc-modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          maxHeight: "90vh",
          overflowY: "auto",
          width: "70%",
          maxWidth: "900px",
        }}
      >
        {loading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div
              className="spinner-border"
              role="status"
              style={{ color: "var(--primary-color)" }}
            ></div>
          </div>
        ) : pdfUrl ? (
          <iframe
            src={pdfUrl}
            title="Notice Document"
            style={{
              width: "100%",
              height: "75vh",
              border: "none",
              borderRadius: "6px",
            }}
          />
        ) : (
          <p className="text-center text-muted py-4">
            Failed to generate document
          </p>
        )}

        {/* Footer button */}
        <div className="d-flex gap-2 mt-3">
          <button
            className="btn btn-badge-border px-3 flex-grow-1"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="btn btn-badge-primary px-3 flex-grow-1"
            style={{
              opacity: canEdit("notices") ? 1 : 0.5,
              pointerEvents: canEdit("notices") ? "auto" : "none",
            }}
            onClick={() => {
              if (canEdit("notices")) {
                navigate(`/notices/edit/${notice.id}`);
              }
            }}
            disabled={!canEdit("notices")}
          >
            Edit Notice
          </button>
        </div>
      </div>
    </div>
  );
};

export default NoticeViewPopUp;
